import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/axios';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { format, isValid } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import UserDetailsModal from './UserDetailsModal';

const { FiSearch, FiChevronLeft, FiChevronRight, FiDownload, FiRefreshCw, FiUser, FiMapPin, FiAward, FiX, FiFilter, FiClock, FiFileText, FiCheckCircle, FiLoader } = FiIcons;

const ExportModal = ({ isOpen, onClose, totalRecords, activeFilters, onDownload }) => {
  const [step, setStep] = useState('confirm'); // confirm, processing, ready
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (step === 'processing') {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setStep('ready');
            return 100;
          }
          return prev + (100 / (totalRecords > 1000 ? 50 : 20));
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [step, totalRecords]);

  if (!isOpen) return null;

  const estimatedTime = Math.max(2, Math.ceil(totalRecords / 500));

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-surface-950/60 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl border border-surface-200"
      >
        <div className="flex justify-between items-start mb-6">
          <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center justify-center">
            <SafeIcon icon={FiDownload} className="w-6 h-6 text-primary-600" />
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface-100 rounded-xl transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5 text-surface-400" />
          </button>
        </div>

        {step === 'confirm' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-black text-surface-900 leading-tight">Export Database</h3>
              <p className="text-sm text-surface-500 mt-1 font-medium">Prepare your CSV export based on current filters.</p>
            </div>
            
            <div className="bg-surface-50 rounded-2xl p-4 border border-surface-100 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-surface-400 uppercase tracking-wider">Total Records</span>
                <span className="text-sm font-black text-primary-600">{totalRecords.toLocaleString()} Users</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-surface-400 uppercase tracking-wider">Estimated Time</span>
                <span className="text-sm font-black text-surface-700">~{estimatedTime} Seconds</span>
              </div>
              <div className="pt-2 border-t border-surface-200/60">
                <p className="text-[10px] font-bold text-surface-400 uppercase mb-2 italic">Active Filters Applied</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(activeFilters).map(([key, val]) => val && (
                    <span key={key} className="px-2 py-0.5 bg-white border border-surface-200 rounded-md text-[9px] font-black text-surface-600 uppercase">
                      {key.replace('filter', '')}: {val}
                    </span>
                  ))}
                  {!Object.values(activeFilters).some(v => v) && <span className="text-[10px] text-surface-400">None (Full Export)</span>}
                </div>
              </div>
            </div>

            <Button className="w-full h-12 rounded-2xl" onClick={() => setStep('processing')}>
              Generate CSV File
            </Button>
          </div>
        )}

        {step === 'processing' && (
          <div className="text-center py-6 space-y-6">
            <div className="relative w-24 h-24 mx-auto">
              <div className="absolute inset-0 rounded-full border-4 border-primary-50"></div>
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="44"
                  fill="transparent"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeDasharray={276}
                  strokeDashoffset={276 - (276 * progress) / 100}
                  className="text-primary-600 transition-all duration-300"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <span className="text-lg font-black text-surface-900">{Math.round(progress)}%</span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-surface-900">Preparing Export...</h3>
              <p className="text-xs text-surface-400 mt-1 font-medium italic">Streaming data from master_users table</p>
            </div>
          </div>
        )}

        {step === 'ready' && (
          <div className="text-center py-4 space-y-6">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto border-4 border-emerald-100">
              <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-xl font-black text-surface-900">Export Ready!</h3>
              <p className="text-sm text-surface-500 mt-1 font-medium">The CSV file containing {totalRecords} records is ready for download.</p>
            </div>
            <Button className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700" onClick={onDownload}>
              <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
              Download CSV Now
            </Button>
            <button onClick={onClose} className="text-xs font-bold text-surface-400 hover:text-surface-600 uppercase tracking-widest">
              Close Window
            </button>
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
};

const Users = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterGift, setFilterGift] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSlab, setFilterSlab] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedUserMobile, setSelectedUserMobile] = useState(null);
  
  const perPage = 20;

  const { data: filterOptions } = useQuery({
    queryKey: ['filter_options'],
    queryFn: async () => {
      const response = await api.get('filter_options.php');
      return response.data?.data || { states: [], gifts: [], slabs: [] };
    },
    staleTime: 300000
  });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['users', page, search, filterGift, filterState, filterStatus, filterSlab, dateFrom, dateTo],
    queryFn: async () => {
      const isSearching = search.trim().length > 0;
      const endpoint = isSearching ? 'search_users.php' : 'users_table.php';
      
      const params = isSearching 
        ? { q: search } 
        : { 
            page, 
            gift: filterGift || undefined, 
            state: filterState || undefined, 
            status: filterStatus || undefined,
            slab: filterSlab || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            limit: perPage
          };

      const response = await api.get(endpoint, { params });
      return response.data;
    },
    keepPreviousData: true,
  });

  const users = data?.data || [];
  const totalPages = data?.total_pages || 1;
  const totalRecords = data?.total_records || (search ? users.length : 0);
  const isSearchActive = search.trim().length > 0;

  const handleDownload = async () => {
    try {
      // Correctly map all current frontend filters and search to export.php params
      const params = {
        q: search,
        gift: filterGift,
        state: filterState,
        status: filterStatus,
        slab: filterSlab,
        date_from: dateFrom,
        date_to: dateTo
      };

      const res = await api.get('export.php', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setIsExportModalOpen(false);
    } catch (err) {
      alert('Export failed. Please check your connection.');
    }
  };

  const resetFilters = () => {
    setSearch('');
    setFilterGift('');
    setFilterState('');
    setFilterStatus('');
    setFilterSlab('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const selectClass = "h-11 px-4 rounded-xl border-2 border-surface-200 text-[11px] font-bold bg-white focus:border-primary-400 outline-none text-surface-700 appearance-none disabled:opacity-50 transition-all cursor-pointer hover:border-surface-300";

  return (
    <div className="space-y-6 pb-20 animate-fade-up">
      {/* Search & Export Header */}
      <div className="flex flex-col xl:flex-row gap-4">
        <div className="relative flex-1 group">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 group-focus-within:text-primary-500 transition-colors" />
          <Input 
            placeholder="Search by Outlet Name or Mobile Number..." 
            className="pl-12 h-14 bg-white border-none shadow-sm rounded-[1.25rem] text-sm focus:ring-4 focus:ring-primary-100"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setIsExportModalOpen(true)}
            disabled={users.length === 0}
            className="h-14 px-8 rounded-[1.25rem] bg-surface-900 hover:bg-black text-white shadow-xl shadow-surface-200/50 flex items-center"
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
            <span className="text-xs font-black uppercase tracking-widest">Export Result</span>
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => refetch()} 
            disabled={isFetching}
            className="h-14 w-14 rounded-[1.25rem] border-white bg-white shadow-sm"
          >
            <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 text-primary-600 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Advanced Filters */}
      <Card className="border-none shadow-sm bg-white/50 backdrop-blur-sm p-4 overflow-visible rounded-[1.5rem]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          <div className="relative">
            <select 
              className={`${selectClass} w-full`}
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              disabled={isSearchActive}
            >
              <option value="">Status: All</option>
              <option value="pending">Pending</option>
              <option value="gift_selected">Gift Selected</option>
              <option value="address_completed">Address Collected</option>
              <option value="shop_name_mismatched">Shop Name Mismatched</option>
              <option value="not_shop_owner">Not Shop Owner</option>
            </select>
            <SafeIcon icon={FiFilter} className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-3 text-surface-300 pointer-events-none" />
          </div>

          <div className="relative">
            <select 
              className={`${selectClass} w-full`}
              value={filterState}
              onChange={(e) => { setFilterState(e.target.value); setPage(1); }}
              disabled={isSearchActive}
            >
              <option value="">State: All</option>
              {filterOptions?.states?.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <SafeIcon icon={FiMapPin} className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-3 text-surface-300 pointer-events-none" />
          </div>

          <div className="relative">
            <select 
              className={`${selectClass} w-full`}
              value={filterGift}
              onChange={(e) => { setFilterGift(e.target.value); setPage(1); }}
              disabled={isSearchActive}
            >
              <option value="">Gift: All</option>
              {filterOptions?.gifts?.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <SafeIcon icon={FiAward} className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-3 text-surface-300 pointer-events-none" />
          </div>

          <div className="relative">
            <select 
              className={`${selectClass} w-full`}
              value={filterSlab}
              onChange={(e) => { setFilterSlab(e.target.value); setPage(1); }}
              disabled={isSearchActive}
            >
              <option value="">Slab: All</option>
              {filterOptions?.slabs?.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <SafeIcon icon={FiFilter} className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-3 text-surface-300 pointer-events-none" />
          </div>

          <div className="flex items-center gap-2 bg-white px-2 rounded-xl border-2 border-surface-200 min-w-0 lg:col-span-2 xl:col-span-2">
            <SafeIcon icon={FiClock} className="w-3 h-3 text-surface-400 ml-1 shrink-0" />
            <Input 
              type="date" 
              className="h-8 border-none bg-transparent text-[10px] font-bold w-full focus:ring-0 p-0 min-w-0"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              disabled={isSearchActive}
            />
            <span className="text-surface-300 text-[9px] font-black uppercase shrink-0"> To </span>
            <Input 
              type="date" 
              className="h-8 border-none bg-transparent text-[10px] font-bold w-full focus:ring-0 p-0 min-w-0"
              value={dateTo}
              min={dateFrom}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              disabled={isSearchActive || !dateFrom}
            />
          </div>
        </div>

        {(filterGift || filterState || filterStatus || filterSlab || dateFrom || dateTo) && (
          <div className="mt-4 pt-4 border-t border-surface-100 flex items-center justify-between">
            <Badge variant="neutral" className="bg-surface-100/50 text-surface-500 font-black text-[9px] uppercase tracking-widest px-3">Active Filtering</Badge>
            <button 
              onClick={resetFilters}
              className="text-[10px] font-black text-red-500 uppercase flex items-center hover:opacity-70"
            >
              <SafeIcon icon={FiX} className="w-3 h-3 mr-1" />
              Reset All Filters
            </button>
          </div>
        )}
      </Card>

      {/* Main Table */}
      <Card className="overflow-hidden border-none shadow-2xl shadow-surface-200/50 rounded-[2rem] bg-white">
        <div className="p-6 border-b border-surface-50 flex items-center justify-between bg-surface-50/30">
          <p className="text-[12px] text-surface-400 font-bold uppercase tracking-wider">Total Records: {totalRecords.toLocaleString()}</p>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-surface-400 uppercase tracking-widest italic">Click row to view full profile</span>
            <span className={`w-2 h-2 rounded-full ${isFetching ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`}></span>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-50/50">
                <th className="px-8 py-5 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Outlet Name</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Mobile No.</th>
                <th className="px-8 py-5 text-center text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Slab</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Gift</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Address</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Sync Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-50">
              {isLoading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}><td colSpan="6" className="px-8 py-6"><Skeleton className="h-12 w-full rounded-2xl" /></td></tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-32 text-center text-surface-400 font-medium italic">No results found for your query.</td>
                </tr>
              ) : (
                users.map((user, idx) => (
                  <tr 
                    key={idx} 
                    onClick={() => setSelectedUserMobile(user.mobile_no)}
                    className="hover:bg-primary-50/30 transition-all duration-300 group cursor-pointer"
                  >
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-surface-100 flex items-center justify-center text-surface-400 font-bold text-xs group-hover:bg-primary-100 group-hover:text-primary-600">
                          {user.outlet_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-bold text-surface-800 text-sm tracking-tight leading-tight">{user.outlet_name || '—'}</span>
                          {user.updated_outlet_name && (
                            <span className="text-[10px] text-primary-500 font-black uppercase tracking-wider mt-0.5">{user.updated_outlet_name}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className="text-[11px] font-mono font-black text-primary-600 bg-primary-50 px-3 py-1.5 rounded-xl border border-primary-100/50">
                        {user.mobile_no}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-center">
                      <span className="text-[10px] font-black uppercase tracking-tighter text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-100">{user.achieved_slab || 'N/A'}</span>
                    </td>
                    <td className="px-8 py-6">
                      {user.selected_gift ? (
                        <Badge variant="accent" className="font-black text-[9px] uppercase px-3 py-1 rounded-lg">{user.selected_gift}</Badge>
                      ) : (
                        <span className="text-[10px] font-bold text-surface-300 uppercase italic">Pending</span>
                      )}
                    </td>
                    <td className="px-8 py-6">
                      <span className="text-xs font-bold text-surface-800">{user.state || '—'}</span>
                    </td>
                    <td className="px-8 py-6 text-surface-500 font-bold text-[11px] uppercase tracking-tighter">
                      {user.updated_date ? format(new Date(user.updated_date), 'MMM dd, yyyy') : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-6 border-t border-surface-50 bg-surface-50/20">
          <div className="flex items-center space-x-2">
            <span className="text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Page</span>
            <div className="bg-white border border-surface-200 rounded-lg px-2 py-1 text-xs font-black text-primary-600">{page} / {totalPages}</div>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline" 
              onClick={() => setPage(p => Math.max(1, p - 1))} 
              disabled={page === 1 || isLoading}
              className="h-11 px-4 rounded-xl border-surface-200 text-xs font-bold"
            >
              Previous
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setPage(p => p + 1)} 
              disabled={page >= totalPages || isLoading}
              className="h-11 px-4 rounded-xl border-surface-200 text-xs font-bold"
            >
              Next
            </Button>
          </div>
        </div>
      </Card>

      {/* Side Profile Modal */}
      <UserDetailsModal mobileNo={selectedUserMobile} onClose={() => setSelectedUserMobile(null)} />

      <ExportModal 
        isOpen={isExportModalOpen} 
        onClose={() => setIsExportModalOpen(false)} 
        totalRecords={totalRecords}
        onDownload={handleDownload}
        activeFilters={{
          search: search || null,
          gift: filterGift,
          state: filterState,
          status: filterStatus,
          slab: filterSlab,
          dateFrom: dateFrom,
          dateTo: dateTo
        }}
      />
    </div>
  );
};

export default Users;